import { memo, useCallback, useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { DateTimePicker } from '@/components/DateTimePicker';
import type { FormFieldBaseProps } from './field-base';

/**
 * Date input backed by the shared DateTimePicker. The form value is an ISO
 * date string "YYYY-MM-DD" matching backend DATE storage; we round-trip
 * through Date (UTC midnight) when handing values to/from the picker.
 */
export interface DateFieldProps extends FormFieldBaseProps<string | null | undefined> {}

function isoToDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

function dateToIso(d: Date | null): string | null {
  if (!d) return null;
  return d.toISOString().slice(0, 10);
}

export const DateField = memo(function DateField({
  field,
  value,
  onChange,
  readOnly = false,
  error,
}: DateFieldProps) {
  const { theme } = useTheme();

  const dateValue = useMemo(() => isoToDate(value), [value]);

  const handleChange = useCallback(
    (next: Date | null) => {
      onChange(dateToIso(next));
    },
    [onChange],
  );

  const errorId = error ? `${field.key}__error` : undefined;
  return (
    <div className={'form-group date-field' + (theme ? ' ' + theme : '') + (error ? ' has-error' : '')}>
      <label className={'field-label' + (field.required ? ' required' : '')} htmlFor={field.key}>
        {field.label}
      </label>
      <DateTimePicker
        value={dateValue}
        type="date"
        disabled={readOnly}
        appendToBody
        inputAttr={{
          id: field.key,
          name: field.key,
          required: field.required === true,
          title: error || undefined,
          'aria-invalid': error ? true : undefined,
          'aria-describedby': errorId,
        }}
        onChange={handleChange}
      />
      {error ? <div id={errorId} className="form-field-error" role="alert">{error}</div> : null}
    </div>
  );
});
